import {
    Box,
    ButtonBase,
    Collapse,
    Divider,
    IconButton,
    lighten,
    Stack,
    Typography,
} from '@mui/material';
import { useGetReservationConflictsMutation } from '../../../../app/Slices/SevenDayApiSlice';
import toast from '../../../shared/Toast';
import React, { useEffect, useMemo, useState } from 'react';
import { camelCaseToTitleCase, formatTime } from '../../../../shared/utils';
import { Status } from '../../../enums/booking';
import {
    ExpandMore,
    OpenInNew,
    ReportProblemOutlined,
} from '@mui/icons-material';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';
import moment from 'moment';
import { theme } from '../../../../shared/theme';

interface ConflictsProps {
    reservation: any;
}

const Conflicts = ({ reservation }: ConflictsProps) => {
    const [getConflicts] = useGetReservationConflictsMutation();
    const [conflicts, setConflicts] = useState<any[]>([]);
    const [open, setOpen] = useState(false);

    const fetchData = async () => {
        try {
            const request = { bookingId: reservation.id };
            const response = await getConflicts(request).unwrap();
            setConflicts(Array.isArray(response) ? response : []);
        } catch {
            toast.error('Error fetching conflicting bookings');
        }
    };

    useEffect(() => {
        if (reservation?.id) fetchData();
    }, [reservation?.id]);

    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key !== 'reservation-updated') return;
            if (!reservation?.id) return;
            fetchData();
        };

        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [reservation?.id]);

    const hasConflicts = conflicts.length > 0;
    const isCommercial = reservation?.module?.type === 'commercial';

    const getStatusSx = (s: string) => {
        switch (s) {
            case 'approved':
                return {
                    bgcolor: lighten(theme.palette.yellow.main, 0.78),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.yellow.main, 0.35),
                };
            case 'pending':
                return {
                    bgcolor: lighten(theme.palette.primary.main, 0.76),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.primary.main, 0.32),
                };
            case 'cancelled':
            case 'declined':
                return {
                    bgcolor: lighten(theme.palette.error.main, 0.82),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.error.main, 0.3),
                };
            case 'completed':
                return {
                    bgcolor: lighten(theme.palette.info.main, 0.82),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.info.main, 0.3),
                };
            default:
                return {
                    bgcolor: theme.palette.custom.cardActionBackground,
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.custom.lightBorderColor,
                };
        }
    };

    const getChipColor = (s: string) => {
        switch (s) {
            case Status.Completed:
                return 'green';
            case Status.Cancelled:
                return 'red';
            case Status.Approved:
                return 'secondary';
            case Status.Declined:
                return 'gray';
            default:
                return 'blue';
        }
    };

    const getSpaceLabel = (row: any) => {
        const spaceType =
            row.bookingInfo?.desks > 0
                ? 'desk'
                : row.bookingInfo?.meetingRooms > 0
                  ? 'meetingRoom'
                  : row.bookingInfo?.privateOffices > 0
                    ? 'privateOffice'
                    : row.bookingInfo?.entireVenue
                      ? 'entireVenue'
                      : 'other';

        return camelCaseToTitleCase(spaceType);
    };

    const formatRange = (row: any) => {
        const d1 = moment(row.startDate).format('ddd D MMM');
        const d2 = moment(row.endDate).format('ddd D MMM');
        const t1 = formatTime(row.bookingInfo?.checkinTime);
        const t2 = formatTime(row.bookingInfo?.checkoutTime);
        return `${d1} ${t1} → ${d2} ${t2}`;
    };

    const attention = useMemo(
        () =>
            conflicts.filter(
                (c: any) =>
                    c.status === Status.Pending || c.status === Status.Approved,
            ),
        [conflicts],
    );

    const fyi = useMemo(
        () =>
            conflicts.filter(
                (c: any) =>
                    c.status !== Status.Pending && c.status !== Status.Approved,
            ),
        [conflicts],
    );

    const openReservation = (id: number) =>
        window.open(
            `/reservations/experience/${id}`,
            '_blank',
            'noopener,noreferrer',
        );

    const SectionTitle = ({ children }: { children: React.ReactNode }) => (
        <Typography
            variant='body3'
            sx={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: 0.35,
                color: theme.palette.text.secondary,
                px: 0.25,
                pb: 0.75,
                textTransform: 'uppercase',
            }}
        >
            {children}
        </Typography>
    );

    const existingReservationText = useMemo(() => {
        const n = conflicts.length;

        if (n === 1)
            return `This reservation overlaps with #${conflicts[0]?.id}`;

        return `This reservation overlaps with ${n} reservations`;
    }, [conflicts]);

    const ConflictRow = ({ c }: { c: any }) => (
        <ButtonBase
            onClick={() => openReservation(c.id)}
            sx={{
                width: '100%',
                textAlign: 'left',
                borderRadius: 2,
                border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                p: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                backgroundColor: theme.palette.neutral.main,
                '&:hover': {
                    backgroundColor: theme.palette.custom.cardActionBackground,
                },
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        flexWrap: 'wrap',
                    }}
                >
                    <Typography sx={{ fontWeight: 900, fontSize: 13.5 }}>
                        #{c.id}
                    </Typography>

                    <CustomChip
                        color={getChipColor(c.status)}
                        label={camelCaseToTitleCase(String(c.status))}
                        variant='outlined'
                        size='small'
                        sx={{
                            ...getStatusSx(c.status),
                            borderWidth: 1,
                            height: 18,
                            px: 0.6,
                            minWidth: 72,
                            borderRadius: 999,
                            '& .MuiChip-label': { px: 0.6 },
                        }}
                    />

                    {isCommercial ? (
                        <CustomChip
                            color='gray'
                            label={
                                <Typography
                                    variant='body3'
                                    sx={{ fontSize: 10.5 }}
                                >
                                    {getSpaceLabel(c)}
                                </Typography>
                            }
                            variant='outlined'
                            size='small'
                        />
                    ) : null}
                </Box>

                <Typography
                    sx={{
                        mt: 0.35,
                        fontSize: 12,
                        fontWeight: 650,
                        color: theme.palette.text.secondary,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {formatRange(c)}
                </Typography>
            </Box>

            <OpenInNew
                fontSize='small'
                sx={{
                    opacity: 0.6,
                    flex: '0 0 auto',
                    fontSize: '14px',
                }}
            />
        </ButtonBase>
    );

    if (!hasConflicts) return null;

    return (
        <Box sx={{ width: '100%' }}>
            <Stack spacing={1}>
                <ButtonBase
                    onClick={() => setOpen((v) => !v)}
                    sx={{
                        width: '100%',
                        borderRadius: 2,
                        border: `1px solid ${lighten(theme.palette.warning.main, 0.45)}`,
                        backgroundColor: lighten(
                            theme.palette.warning.main,
                            0.9,
                        ),
                        px: 1.5,
                        py: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5,
                        textAlign: 'left',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                            backgroundColor: lighten(
                                theme.palette.warning.main,
                                0.88,
                            ),
                        },
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 4,
                            backgroundColor: theme.palette.warning.main,
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                        }}
                    >
                        <ReportProblemOutlined
                            fontSize='small'
                            sx={{
                                color: theme.palette.warning.main,
                                mt: '2px',
                            }}
                        />

                        <Box
                            sx={{
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                            }}
                        >
                            <Typography
                                component='div'
                                variant='body3'
                                sx={{
                                    fontWeight: 900,
                                    fontSize: 12.5,
                                    color: theme.palette.warning.main,
                                    lineHeight: 1.15,
                                    display: 'block',
                                    width: '100%',
                                }}
                            >
                                Overlap detected
                            </Typography>

                            <Typography
                                component='div'
                                variant='body3'
                                sx={{
                                    mt: 0.35,
                                    fontSize: 12,
                                    fontWeight: 650,
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.2,
                                    display: 'block',
                                    width: '100%',
                                }}
                            >
                                {existingReservationText}
                            </Typography>
                        </Box>
                    </Box>

                    <IconButton
                        size='small'
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpen((v) => !v);
                        }}
                        sx={{
                            borderRadius: 999,
                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                            backgroundColor: theme.palette.neutral.main,
                            alignSelf: 'center',
                            '&:hover': {
                                backgroundColor:
                                    theme.palette.custom.cardActionBackground,
                            },
                        }}
                    >
                        <ExpandMore
                            sx={{
                                fontSize: '14px',
                                transition: 'transform 160ms ease',
                                transform: open
                                    ? 'rotate(180deg)'
                                    : 'rotate(0deg)',
                            }}
                        />
                    </IconButton>
                </ButtonBase>

                <Collapse in={open}>
                    <Box
                        sx={{
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                            overflow: 'hidden',
                            backgroundColor: theme.palette.neutral.main,
                        }}
                    >
                        <Box sx={{ p: 1.5 }}>
                            <SectionTitle>Other reservations</SectionTitle>

                            {attention.length ? (
                                <>
                                    <Stack spacing={0.85}>
                                        {attention.map((c: any) => (
                                            <ConflictRow key={c.id} c={c} />
                                        ))}
                                    </Stack>

                                    {fyi.length ? (
                                        <Divider sx={{ my: 1.5 }} />
                                    ) : null}
                                </>
                            ) : null}

                            {fyi.length ? (
                                <Stack spacing={0.85}>
                                    {fyi.map((c: any) => (
                                        <ConflictRow key={c.id} c={c} />
                                    ))}
                                </Stack>
                            ) : null}
                        </Box>
                    </Box>
                </Collapse>
            </Stack>
        </Box>
    );
};

export default Conflicts;
